import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// ---------------------------------------------------------------------------
// Request interfaces
// ---------------------------------------------------------------------------

export interface Idrv5QuoteRequest {
  // Reserved for future use if iDrv5 exposes a quote-creation endpoint.
  // The current integration only reads existing quotes via getQuoteById().
  [key: string]: any;
}

// ---------------------------------------------------------------------------
// Response interfaces — matches GET /api/get_quote_details/:id
// ---------------------------------------------------------------------------

export interface Idrv5QuoteHeader {
  id: number;
  quote_number: string;
  company_name: string;
  full_name: string | null;
  email: string;
  mobile_no: string;
  flat_rate: string;
  base_rate: string;
  total_price_amount: string | null;
  gst_amount: string | null;
  gst_id: number | null;
  gst_percentage: string | null;
  currency: string | null;
  customer_id: number | null;
  rate_entry_id: number;
  pieces: number;
  pickup_date: string;
  delivery_date: string;
  origin_premise_type: number;
  destination_premise_type: number;
  origin_is_taillift: number;
  destination_is_taillift: number;
  special_instructions: string | null;
  status: string;
  is_email_sent: string | null;
  created_at: string;
  updated_at: string;
  // Origin address — not yet returned by the API; will be populated once
  // the backend implements these fields.
  origin_suburb?: string | null;
  origin_postcode?: string | null;
  origin_state?: string | null;
  // Destination address — same as above.
  destination_suburb?: string | null;
  destination_postcode?: string | null;
  destination_state?: string | null;
}

export interface Idrv5DgLicense {
  id: number;
  packaging_id: number;
  quote_id: number;
  dg_license_id: number;
  dg_name: string;
  dg_description: string;
  created_at: string;
}

export interface Idrv5QuotePackaging {
  id: number;
  quote_id: number;
  packaging_type_id: number;
  packaging_type_name: string;
  packaging_type_category: string;
  quantity: number;
  length: string;   // decimal strings from the API
  width: string;
  height: string;
  weight: string;
  is_oversized?: boolean;  // flag to indicate oversized packaging
  dg_licenses: Idrv5DgLicense[];
  created_by: number;
  updated_by: number;
  created_at: string;
  updated_at: string;
}

export interface Idrv5QuoteRateEntry {
  id: number;
  quote_id: number;
  rate_entry_id: number;
  rate_card_name: string;
  service_level_id: number;
  service_level_name: string | null;
  service_level_description: string;
  base_rate: string;
  base_cost_multiplier: string;
  transit_time_multiplier: string;
  transit_times_service_level_id: number;
  transit_days: number | null;
  transit_hours: number | null;
  business_days: number;
  business_days_returns: number;
  origin_zone_id: number;
  destination_zone_id: number;
  is_selected: boolean;
  rate_entry_created_at: string;
  // Note: the API also returns this key with a trailing space ("rate_entry_grand_amount ")
  // but we use the clean key only.
  rate_entry_grand_amount?: string | null;
  created_at: string;
}

export interface Idrv5RateEntry {
  id: number;
  rate_card_id: number;
  rate_card_name: string;
  display_name: string;
  calculation_method_name: string;
  charging_type: string;
  rate_calculation_method_id: number;
  base_rate: string;
  flat_rate: string | null;
  load_rate: string | null;
  drop_rate: string | null;
  rate_per_km: string | null;
  rate_per_tonne: string | null;
  chargeable_weight_rate: string | null;
  minimum_rate: string | null;
  maximum_rate: string | null;
  minimum_hours: string | null;
  out_of_gauge_charge: string | null;
  oversize_charge_priority: string | null;
  max_height_cm: string | null;
  distance_km: string | null;
  vehicle_type_id: number | null;
  rate_schedule_id: number | null;
  origin_zone_id: number;
  destination_zone_id: number;
  created_at: string;
  updated_at: string;
}

export interface Idrv5QuoteDetailsResponse {
  quote: Idrv5QuoteHeader;
  quote_packagings: Idrv5QuotePackaging[];
  quote_rate_entries: Idrv5QuoteRateEntry[];
  rate_entries: Idrv5RateEntry[];
  quote_addons: any[];
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({
  providedIn: 'root'
})
export class Idrv5ApiService {
  private apiBaseUrl = environment.idrv5ApiUrl || 'https://omega-idrv5.up.railway.app/api';

  // No API key required for this endpoint — matches the Dyspach pattern where
  // the key is only needed for outbound quote-creation calls.
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) { }

  /**
   * Fetch a previously created iDrv5 quote by its numeric ID.
   * Calls GET /api/get_quote_details/:id on the iDrv5 backend.
   * Mirrors DyspachApiService.getQuoteById().
   */
  getQuoteById(quoteId: string | number): Observable<Idrv5QuoteDetailsResponse> {
    return this.http.get<Idrv5QuoteDetailsResponse>(
      `${this.apiBaseUrl}/get_quote_details/${quoteId}`,
      this.httpOptions
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  // Error handling — identical to DyspachApiService.handleError
  private handleError(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    console.error(errorMessage);
    return throwError(errorMessage);
  }
}
